
import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Loader2, ExternalLink } from 'lucide-react';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import ThemeSwitcher from '@/components/common/ThemeSwitcher';

const Auth: React.FC = () => {
  console.log('Auth component rendering...');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const { signIn, user, session, loading } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  console.log('Auth render - user:', !!user, 'session:', !!session, 'loading:', loading);

  useEffect(() => {
    console.log('Auth useEffect - user:', user?.id);
    
    // If user is signed in, always go to dashboard
    if (user) {
      console.log('User authenticated, navigating to home...');
      navigate('/');
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Sign in attempt...');
    setIsLoading(true);
    
    try {
      const { error } = await signIn(email, password);
      if (error) {
        console.error('Sign in error:', error);
        toast.error(error.message);
      } else {
        console.log('Sign in successful');
      }
    } catch (err) {
      console.error('Sign in exception:', err);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResettingPassword(true);

    try {
      const currentDomain = window.location.origin;
      console.log('Sending reset email with redirect to:', `${currentDomain}/auth#reset-password`);
      
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${currentDomain}/`,
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(t('auth.resetEmailSent'));
        setShowForgotPassword(false);
        setResetEmail('');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      toast.error('An unexpected error occurred');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }

    if (newPassword.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('Password update error:', error);
        toast.error(error.message);
      } else {
        toast.success(t('auth.passwordUpdateSuccess'));
        setIsPasswordReset(false);
        // Clear the URL parameters and hash
        window.history.replaceState({}, document.title, window.location.pathname);
        // Navigate to dashboard after successful password reset
        navigate('/');
      }
    } catch (err) {
      console.error('Password update exception:', err);
      toast.error('An unexpected error occurred');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  console.log('Auth component rendering form...');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4 relative">
      {/* Settings controls in top-right corner */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <div className="p-1 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
          <LanguageSwitcher />
        </div>
        <div className="p-1 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
          <ThemeSwitcher />
        </div>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">StudioSync</CardTitle>
          <CardDescription className="text-center">
            {t('auth.welcomeMessage')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <div className="space-y-4">
              <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('auth.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('auth.signIn')}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full" 
                onClick={() => setShowForgotPassword(true)}
              >
                {t('auth.forgotPassword')}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="w-full" 
                onClick={() => {
                  const locale = i18n.language === 'el' ? 'el' : 'en';
                  window.open(`https://try.studio-sync.app/${locale}`, '_blank');
                }}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('auth.learnMore')}
              </Button>
            </form>

            {/* Forgot Password Modal/Form */}
            {showForgotPassword && (
              <div className="mt-4 p-4 border rounded-lg bg-muted/50">
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">{t('auth.email')}</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button type="submit" className="flex-1" disabled={isResettingPassword}>
                      {isResettingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('auth.sendResetEmail')}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmail('');
                      }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </form>
              </div>
            )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
